import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { BASE_URL } from "../services/config";
import StoresMap from '../components/StoresMap';
import api from '../services/api';
import { Icon } from '@iconify/react';

const StoreAddressesPage: React.FC = () => {
  const location = useLocation();
  const [stores, setStores] = React.useState<any[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedStoreId, setSelectedStoreId] = React.useState<number | null>(null);
  const [query, setQuery] = React.useState<string>('');
  const [onlyPickup, setOnlyPickup] = React.useState<boolean>(false);

  const yandexApiKey = (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env.VITE_YANDEX_MAPS_API_KEY) ? (import.meta as any).env.VITE_YANDEX_MAPS_API_KEY : undefined;

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get('/stores/');
        const data = resp?.data;
        if (cancelled) return;
        if (Array.isArray(data)) setStores(data);
        else if (Array.isArray(data?.results)) setStores(data.results);
        else setStores([]);
      } catch (e: any) {
        console.error('Stores fetch error', e);
        if (!cancelled) setError(e?.response?.data?.message || 'Ошибка при загрузке точек');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Note: we don't load full store details for now — selection is handled on the map/list only

  const filteredStores = React.useMemo(() => {
    if (!stores) return null;
    const q = query.trim().toLowerCase();
    return stores.filter(s => {
      if (onlyPickup && s.type && s.type !== 'pickup') return false;
      if (!q) return true;
      const name = String(s.name || '').toLowerCase();
      const address = String(s.address || '').toLowerCase();
      return name.includes(q) || address.includes(q) || String(s.phone || '').includes(q);
    });
  }, [stores, query, onlyPickup]);

  const handleSelect = (id: number | string) => {
    setSelectedStoreId(Number(id));
  };

  const renderCard = (s: any) => {
    return (
      <div key={s.id} className={`p-3 border ${selectedStoreId === s.id ? 'border-primary bg-primary/5' : 'border-divider bg-content2'}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="font-medium text-sm">{s.name}</div>
            <div className="text-foreground/70 text-xs mt-1">{s.address}</div>
            {s.work_hours && <div className="text-foreground/70 text-xs mt-1">Часы работы: {s.work_hours}</div>}
            {s.phone && <div className="text-foreground/70 text-xs mt-1">Телефон: <a href={`tel:${s.phone}`} className="text-primary">{s.phone}</a></div>}
          </div>
          <div className="flex flex-col items-end gap-2">
            <button onClick={() => handleSelect(s.id)} className="text-sm px-3 py-1 rounded-md bg-primary text-white">На карте</button>
            <a
              href={getDirectionsLink(s)}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-primary underline"
            >Как добраться</a>
          </div>
        </div>
      </div>
    );
  };

  function getDirectionsLink(s: any) {
    // Prefer lat/lon if present, otherwise try to use address string for Yandex query
    const lat = s.latitude ?? s.lat ?? s.location?.lat;
    const lon = s.longitude ?? s.lon ?? s.location?.lon;
    if (lat && lon) return `https://yandex.ru/maps/?rtext=~${lat},${lon}`;
    if (s.address) return `https://yandex.ru/maps/?text=${encodeURIComponent(s.address)}`;
    return '#';
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Торговые точки — SAFONOVA</title>
        <meta name="description" content="Адреса точек продаж и пунктов выдачи SAFONOVA — найдите ближайший магазин, часы работы и как добраться." />
        <link rel="canonical" href={`${BASE_URL || ''}${location?.pathname || '/stores'}`} />
        <meta property="og:title" content="Торговые точки — SAFONOVA" />
        <meta property="og:description" content="Адреса точек продаж и пунктов выдачи SAFONOVA — найдите ближайший магазин, часы работы и как добраться." />
        <meta property="og:url" content={`${BASE_URL || ''}${location?.pathname || '/stores'}`} />
      </Helmet>
      <header className="bg-gradient-to-r from-primary/10 to-transparent py-8">
        <div className="container mx-auto px-6">
          <h1 className="text-3xl font-semibold">Торговые точки</h1>
          <p className="text-foreground/70 mt-2">Найдите ближайшую точку выдачи или магазин — просмотрите адреса, часы работы и как добраться.</p>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Search + list */}
        <aside className="lg:col-span-3 order-2 lg:order-1">
          <div className="p-4 bg-background/60 backdrop-blur-sm shadow-sm sticky top-6">
            <div className="mb-3">
              <label className="block text-xs text-foreground/70 mb-1">Поиск</label>
              <div className="flex items-center gap-2">
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Название, адрес или телефон" className="w-full input px-3 py-2 text-sm" />
                <button onClick={() => { setQuery(''); }} className="p-2 text-foreground/70 hover:text-foreground">
                  <Icon icon="lucide:x" width={16} />
                </button>
              </div>
            </div>

            <div className="space-y-3 max-h-[60vh] overflow-auto pr-2 mt-6">
              {!stores && !error && <div className="text-foreground/60">Загрузка...</div>}
              {error && <div className="text-danger">{error}</div>}
              {filteredStores && filteredStores.length === 0 && <div className="text-foreground/70">Ничего не найдено.</div>}
              {filteredStores && filteredStores.map(s => renderCard(s))}
            </div>
          </div>
        </aside>

        {/* Center: Map */}
        <section className="lg:col-span-9 order-1 lg:order-2">
          <div className="w-full overflow-hidden border border-divider">
            <StoresMap apiKey={yandexApiKey} stores={filteredStores || []} selectedStoreId={selectedStoreId} onSelectStore={(id) => setSelectedStoreId(id)} />
          </div>
        </section>
        {/* Right details removed — map expanded to the right per design request */}

      </main>
    </div>
  );
};

export default StoreAddressesPage;
